// Pure, dependency-free logic for the Speckle-copy toasts, split out from the composable so it can
// be unit-tested without pulling in Apollo or vue-toastification.

export const COPYING = "copying";
export const FAILED = "failed";

export type SpeckleCopyRecord = {
  projectId: string;
  state?: string | null;
  errorMessage?: string | null;
  project?: { name?: string | null } | null;
};

/** What the toast layer should do about one project, derived from two consecutive subscription results. */
export type SpeckleCopyTransition =
  | { projectId: string; kind: "started"; projectName: string | null }
  | { projectId: string; kind: "succeeded"; projectName: string | null }
  | { projectId: string; kind: "failed"; projectName: string | null; errorMessage: string | null };

const nameOf = (record: SpeckleCopyRecord | undefined): string | null => record?.project?.name ?? null;

const byProject = (records: readonly SpeckleCopyRecord[]): Map<string, SpeckleCopyRecord> =>
  new Map(records.map((record) => [record.projectId, record]));

/**
 * The transitions between two subscription results.
 *
 * The row semantics come from the backend and mirror the IFC-upload table's: a row in state
 * `copying` means the job is running, a row in state `failed` means it gave up, and the row
 * LEAVING means it succeeded — the job deletes its own row, and the 15-minute view drops one it
 * never got to delete.
 *
 * A project seen for the first time already in `failed` still reports "failed": arriving on a page
 * after a copy died should say so rather than stay silent. A project seen for the first time in
 * `copying` reports "started", which is what makes a reload mid-copy pick the toast back up.
 */
export function speckleCopyTransitions(
  previous: readonly SpeckleCopyRecord[],
  current: readonly SpeckleCopyRecord[]
): SpeckleCopyTransition[] {
  const before = byProject(previous);
  const after = byProject(current);
  const transitions: SpeckleCopyTransition[] = [];

  for (const [projectId, record] of after) {
    const was = before.get(projectId);
    if (was?.state === record.state) continue;

    if (record.state === COPYING) {
      transitions.push({ projectId, kind: "started", projectName: nameOf(record) });
    } else if (record.state === FAILED) {
      transitions.push({
        projectId,
        kind: "failed",
        projectName: nameOf(record),
        errorMessage: record.errorMessage ?? null,
      });
    }
  }

  for (const [projectId, record] of before) {
    // Gone from the list. Only a copy that was RUNNING counts as succeeded — a row that was
    // already `failed` and then aged out of the 15-minute view has not suddenly worked.
    if (after.has(projectId) || record.state !== COPYING) continue;
    transitions.push({ projectId, kind: "succeeded", projectName: nameOf(record) });
  }

  return transitions;
}

export const copyingMessage = (projectName: string | null): string =>
  projectName ? `Copying Speckle Project for “${projectName}”…` : "Copying Speckle Project…";

export const copiedMessage = (projectName: string | null): string =>
  projectName ? `Speckle project copied for “${projectName}”` : "Speckle project copied";

export const copyFailedMessage = (projectName: string | null): string =>
  projectName
    ? `Could not copy the Speckle project for “${projectName}”. The project is fine — push a model to fill the 3D viewer.`
    : "Could not copy the Speckle project. The project is fine — push a model to fill the 3D viewer.";
